/**
 * @typedef {import('../../shared/PropertyService/Valuation').default} Valuation
 * @typedef {import('../../shared/PropertyService/Valuations').ValuationKeys} ValuationKeys
 * @typedef {import('../../shared/PropertyService/Valuations').default} Valuations
 * @typedef {import('../../../shared/ThemeProvider/Context').PropType} ThemeContext
 * @typedef {{ readonly appTitle?: string | null }} AppTitleTheme
 */
import { translate } from '../../Internationalization';
import formatCentsForDisplay from '../../../utils/formatCentsForDisplay';
import FormErrorMessages from '../../../shared/Forms/Messages/FormErrorMessages';
import FormSuccessMessages from '../../../shared/Forms/Messages/FormSuccessMessages';
import { withTheme } from '../../../shared/ThemeProvider';
import React from 'react';

/**
 * @typedef {{
 * 	readonly isRequestingAccurateValuation: boolean;
 * 	readonly onRequestAccurateValuation: () => void;
 * 	readonly requestStatus: 'error' | 'success' | null;
 * 	readonly valuations: Valuations
 * } & ThemeContext} Props
 */

/** @extends {React.Component<Props>} */
class PropertyValuationSummary extends React.Component {
	/**
	 * @param {ValuationKeys} provider
	 * @returns {string}
	 */
	#getProviderName(provider) {
		if (provider === 'quantariumPrivateLabel') {
			const theme = /** @type {AppTitleTheme} */ (this.props.theme);

			return theme.appTitle ?? 'HomeKeepr';
		}

		return translate(
			'homeowner.pages.finances.view.estimate.providers.quantarium'
		);
	}

	/**
	 * @param {Valuation[]} valuationList
	 * @returns {Valuation | null}
	 */
	static #getLatestValuation(valuationList) {
		const datedValuation = [...valuationList]
			.filter((valuation) => valuation.date !== null)
			.sort(
				(a, b) => (b.date?.valueOf() ?? 0) - (a.date?.valueOf() ?? 0)
			)[0];

		if (datedValuation) {
			return datedValuation;
		}

		return [...valuationList].reverse().find(Boolean) ?? null;
	}

	/**
	 * @returns {(JSX.Element | null)[]}
	 */
	#cards() {
		return [...this.props.valuations.entries()].map(
			/**
			 * @param {[ValuationKeys, Valuation[]]} entry
			 * @returns {JSX.Element | null}
			 */
			([provider, valuationList]) => {
				const valuation =
					PropertyValuationSummary.#getLatestValuation(valuationList);

				if (valuation === null) {
					return null;
				}

				const hasRange =
					valuation.valueLow !== null && valuation.valueHigh !== null;

				return (
					<section
						className="card card-border flex flex-col justify-between h-full p-8"
						key={provider}
					>
						<header className="mb-6">
							<p className="text-sm uppercase text-grey-700 mb-2">
								{this.#getProviderName(provider)}
							</p>
							<h3 className="h-ms-1">
								{translate(
									'homeowner.pages.finances.view.estimate.today_value'
								)}
							</h3>
						</header>

						<div className="h-ms-5 mb-4">
							{formatCentsForDisplay(valuation.valueMean, 0)}
						</div>

						<p className="text-grey-700 min-h-[1.5rem]">
							{hasRange
								? `${formatCentsForDisplay(
										valuation.valueLow,
										0
								  )} - ${formatCentsForDisplay(
										valuation.valueHigh,
										0
								  )}`
								: '\u00A0'}
						</p>
					</section>
				);
			}
		);
	}

	/**
	 * @returns {JSX.Element}
	 */
	render() {
		const requestStatusMessage =
			this.props.requestStatus === 'success' ? (
				<FormSuccessMessages
					className="w-full"
					messages={[
						translate(
							'homeowner.pages.finances.view.estimate.request.success'
						),
					]}
				/>
			) : this.props.requestStatus === 'error' ? (
				<FormErrorMessages
					className="w-full"
					messages={[
						translate(
							'homeowner.pages.finances.view.estimate.request.error'
						),
					]}
				/>
			) : null;

		return (
			<section className="grid gap-6">
				<section className="grid gap-6 md:grid-cols-2">
					{this.#cards()}
				</section>

				<section className="grid gap-4 justify-items-start">
					<button
						className="button button-icon"
						disabled={this.props.isRequestingAccurateValuation}
						onClick={this.props.onRequestAccurateValuation}
						type="button"
					>
						{this.props.isRequestingAccurateValuation
							? translate(
									'homeowner.pages.finances.view.estimate.request.submitting'
							  )
							: translate(
									'homeowner.pages.finances.view.estimate.request.button'
							  )}
					</button>

					{requestStatusMessage}
				</section>

				<section className="form-alert form-alert-grey w-full">
					<p className="mb-3">
						{translate(
							'homeowner.pages.finances.view.estimate.disclaimer_body'
						)}
					</p>
					<p className="font-bold">
						{translate(
							'homeowner.pages.finances.view.estimate.disclaimer_contact'
						)}
					</p>
				</section>
			</section>
		);
	}
}

export default withTheme(PropertyValuationSummary);
